import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';

interface NavigationContextType {
    registerMobileNavClose: (closeFn: () => void) => void;
    registerDesktopNavClose: (closeFn: () => void) => void;
    closeAllNavigation: () => void;
}

const NavigationContext = React.createContext<NavigationContextType>({
    registerMobileNavClose: () => {},
    registerDesktopNavClose: () => {},
    closeAllNavigation: () => {},
});

export const useNavigationContext = (): NavigationContextType => {
    const context = React.useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigationContext must be used within a NavigationProvider');
    }
    return context;
};

export const NavigationProvider = ({ children }: React.PropsWithChildren<unknown>): JSX.Element => {
    const location = useLocation();
    const mobileNavCloseRef = React.useRef<(() => void) | null>(null);
    const desktopNavCloseRef = React.useRef<(() => void) | null>(null);

    const registerMobileNavClose = React.useCallback((closeFn: () => void) => {
        mobileNavCloseRef.current = closeFn;
    }, []);

    const registerDesktopNavClose = React.useCallback((closeFn: () => void) => {
        desktopNavCloseRef.current = closeFn;
    }, []);

    const closeAllNavigation = React.useCallback(() => {
        // Close mobile navigation
        if (mobileNavCloseRef.current) {
            mobileNavCloseRef.current();
        }

        // Close desktop navigation
        if (desktopNavCloseRef.current) {
            desktopNavCloseRef.current();
        }

        // Also trigger the native menu hide functionality
        // The navigation-menu script exposes this globally when mounted
        const allMenuItems = document.querySelectorAll('[data-menu="item"]');
        allMenuItems.forEach((item) => {
            if (item instanceof HTMLElement) {
                const trigger = item.querySelector('[data-menu="trigger"]');
                const content = item.querySelector('[data-menu="content"]');
                if (trigger instanceof HTMLElement && content && trigger.dataset.state === 'open') {
                    // Simulate the hideMenu functionality from the navigation script
                    const mouseLeaveEvent = new Event('mouseleave');
                    const parentNav = item.closest('[data-menu-root-scope-id]');
                    if (parentNav) {
                        parentNav.dispatchEvent(mouseLeaveEvent);
                    }
                }
            }
        });
    }, []);

    // Global click listener to handle same-path navigation closing
    React.useEffect(() => {
        const handleLinkClick = (event: MouseEvent): void => {
            const target = event.target as HTMLElement;
            const link = target.closest('a[href]') as HTMLAnchorElement;

            if (link && location.pathname) {
                const href = link.getAttribute('href');
                if (href) {
                    // Normalize paths by removing trailing slashes and comparing
                    const normalizedCurrentPath = location.pathname.replace(/\/$/, '') || '/';
                    const normalizedLinkPath = href.replace(/\/$/, '') || '/';

                    if (normalizedCurrentPath === normalizedLinkPath) {
                        event.preventDefault();
                        closeAllNavigation();
                    }
                }
            }
        };

        document.addEventListener('click', handleLinkClick);
        return () => document.removeEventListener('click', handleLinkClick);
    }, [location.pathname, closeAllNavigation]);

    const value = React.useMemo(() => ({
        registerMobileNavClose,
        registerDesktopNavClose,
        closeAllNavigation,
    }), [registerMobileNavClose, registerDesktopNavClose, closeAllNavigation]);

    return (
        <NavigationContext.Provider value={value}>
            {children}
        </NavigationContext.Provider>
    );
};

export default NavigationContext;
